import { FieldType } from "../../type-system/FieldType";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import { ConnectionModelSpecVersion, buildConnectionSpec } from "./ShopifyMetadata";
import {
  ShopifyDraftOrderKey,
  ShopifyDraftOrderPlatformDiscountAllocationKey,
  ShopifyDraftOrderPlatformDiscountKey,
  ShopifyDraftOrderPlatformDiscountToDraftOrderPlatformDiscountAllocationKey,
  ShopifyDraftOrderPlatformDiscountToShopKey,
  ShopifyDraftOrderToDraftOrderPlatformDiscountKey,
  ShopifyShopKey,
} from "./keys";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyDraftOrderPlatformDiscountKey,
  "2024-07": {
    name: "Shopify Draft Order Platform Discount",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "Draft Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderToDraftOrderPlatformDiscountKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "draftOrder",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyDraftOrderKey,
        },
      },
      {
        name: "Allocations",
        fieldType: FieldType.HasMany,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "allocations",
            graphQLSelection: `
          id
          quantity
          reductionAmount {
            amount
            currencyCode
          }
          reductionAmountSet {
            presentmentMoney {
                currencyCode
                amount
            }
            shopMoney {
                currencyCode
                amount
              }
            }
          }
          target {
            ... on DraftOrderLineItem {
              draftOrderLineItemId: id
            }
            ... on ShippingLine {
              shippingLineId: id
            }
          }
          `,
          },
        },
        configuration: {
          relatedModelKey: ShopifyDraftOrderPlatformDiscountAllocationKey,
          inverseFieldKey: ShopifyDraftOrderPlatformDiscountToDraftOrderPlatformDiscountAllocationKey,
        },
      },
      {
        name: "Automatic Discount",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "automaticDiscount" } },
      },
      {
        name: "Bxgy Discount",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "bxgyDiscount" } },
      },
      {
        name: "Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "code" } },
      },
      {
        name: "discountClass",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "discountClass" } },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [{ name: "ORDER" }, { name: "PRODUCT" }, { name: "SHIPPING" }],
        },
      },
      {
        name: "Presentation Level",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "presentationLevel" } },
      },
      {
        name: "Short Summary",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "shortSummary" } },
      },
      {
        name: "Summary",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "summary" } },
      },
      {
        name: "title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "title" } },
      },
      {
        name: "Total Amount",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalAmount",
          },
        },
      },
      {
        name: "Total Amount Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "totalAmountPriceSet",
          },
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyDraftOrderPlatformDiscountToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    // synced as part of ShopifyDraftOrder
    syncConfig: { type: "inherit" },
  },
};

export const ShopifyDraftOrderPlatformDiscount = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) =>
  buildConnectionSpec(specs, apiVersion);
